import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService, { CanceledSmartleadAccount } from '../services/api';

export const SmartleadCancels: React.FC = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<CanceledSmartleadAccount[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('canceled_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchCanceledAccounts();
  }, [pagination.page, pagination.limit, sortBy, sortOrder]);

  const fetchCanceledAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getSmartleadCancels({
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder,
      });
      setAccounts(response.data);
      setPagination(response.pagination);
    } catch (e: any) {
      setError(e?.message || 'Failed to load canceled accounts');
    } finally {
      setLoading(false);
    }
  };

  const parseEmailList = (value?: string): string[] => {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {}
    return [];
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (_) {
      return dateStr;
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  if (!user) {
    return (
      <div className="card-modern p-6">
        <h1 className="text-xl font-semibold">Access Denied</h1>
        <p className="text-sm text-muted-foreground mt-2">Please log in to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Smartlead Canceled Accounts</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Track all canceled Smartlead email accounts and their cancellation dates.
        </p>
      </div>

      {error && (
        <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg border border-destructive/20">
          {error}
        </div>
      )}

      <div className="card-modern">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Total: <span className="font-semibold text-foreground">{pagination.total}</span> canceled accounts
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page === 1 || loading}
              className="btn-secondary px-3 py-1 text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
              disabled={pagination.page >= pagination.totalPages || loading}
              className="btn-secondary px-3 py-1 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70"
                  onClick={() => handleSort('domain_name')}
                >
                  <div className="flex items-center gap-1">
                    Domain
                    {sortBy === 'domain_name' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Active Accounts
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Canceled Accounts
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70"
                  onClick={() => handleSort('canceled_date')}
                >
                  <div className="flex items-center gap-1">
                    Canceled Date
                    {sortBy === 'canceled_date' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Client
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Created
                    {sortBy === 'created_at' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                    Loading...
                  </td>
                </tr>
              ) : accounts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                    No canceled accounts found
                  </td>
                </tr>
              ) : (
                accounts.map((account) => {
                  const activeAccounts = parseEmailList(account.accounts_to_create);
                  const canceledAccounts = parseEmailList(account.accounts_to_cancel);

                  return (
                    <tr key={account.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium">
                        {account.domain_name}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {activeAccounts.length > 0 ? (
                          <div className="space-y-1">
                            {activeAccounts.map((email, idx) => (
                              <div key={idx} className="text-xs text-muted-foreground">
                                {email}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {canceledAccounts.length > 0 ? (
                          <div className="space-y-1">
                            {canceledAccounts.map((email, idx) => (
                              <div key={idx} className="text-xs text-destructive">
                                {email}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-destructive font-medium">
                          {formatDate(account.canceled_date)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {account.client_name && (
                          <div className="font-medium">{account.client_name}</div>
                        )}
                        {account.client_email && (
                          <div className="text-xs text-muted-foreground">{account.client_email}</div>
                        )}
                        {!account.client_name && !account.client_email && (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(account.created_at)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-border flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {accounts.length} of {pagination.total} canceled accounts
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Per page:</label>
            <select
              value={pagination.limit}
              onChange={(e) => setPagination((prev) => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
              className="input-modern h-8 w-20 px-2 text-sm"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartleadCancels;

